import { useMemo, useRef } from "react";
import { toast } from "sonner";
import debounce from "lodash/debounce";

import FluidImage from "@/components/images/fluid-image";
import { useCartMutations } from "./hooks/useCartMutations";
import { FLUID_CDN_URL } from "@/lib/constants";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { CartResponse } from "@/networking/carts.api";
import { isApiError } from "@/lib/api-helpers";

export default function ItemCountButton({
  quantity,
  variantId,
  cartToken,
  deleteOnly,
  itemId,
  paymentMethodsIncludes,
  isSubscriptionPending = false,
}: {
  quantity: number;
  variantId?: number;
  cartToken: string;
  deleteOnly?: boolean;
  itemId: number;
  paymentMethodsIncludes: string | null;
  isSubscriptionPending?: boolean;
}) {
  const queryClient = useQueryClient();
  const { updateItemQuantity, deleteItem, removeEnrollmentItem } =
    useCartMutations(cartToken, paymentMethodsIncludes);
  const isUpdatingQuantity =
    useIsMutating({
      mutationKey: ["update-item-quantity", cartToken],
    }) > 0;

  const actionDisabled = isSubscriptionPending || isUpdatingQuantity;

  const rollbackQuantityRef = useRef<number | null>(null);

  const debouncedUpdateQuantity = useMemo(
    () =>
      debounce((newQuantity: number) => {
        const originalQuantity = rollbackQuantityRef.current;

        updateItemQuantity.mutate(
          {
            variantId: variantId,
            quantity: newQuantity,
            itemId: itemId,
          },
          {
            onError: (error: unknown) => {
              console.error("Quantity update error:", error);
              let errorMessage =
                "We were unable to update the quantity. Please try again.";
              if (isApiError(error) && error.data?.error_message) {
                errorMessage = error.data.error_message;
              }
              toast.error(errorMessage);

              if (originalQuantity !== null) {
                queryClient.setQueryData<CartResponse>(
                  ["cart", cartToken, paymentMethodsIncludes],
                  (old) => {
                    if (!old) return old;
                    return {
                      ...old,
                      cart: {
                        ...old.cart,
                        items: old.cart.items.map((item) =>
                          item.id === itemId
                            ? { ...item, quantity: originalQuantity }
                            : item,
                        ),
                      },
                    };
                  },
                );
              }

              rollbackQuantityRef.current = null;
            },
            onSuccess: () => {
              rollbackQuantityRef.current = null;
            },
          },
        );
      }, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variantId, itemId, cartToken, paymentMethodsIncludes, queryClient],
  );

  const handleQuantityChange = (delta: number) => {
    // Disable quantity updates while subscription is pending
    if (isSubscriptionPending) return;

    const newQuantity = Math.max(0, quantity + delta);
    if (newQuantity === 0) {
      deleteItem.mutate({
        itemId: itemId,
      });
    } else {
      // Capture the original quantity BEFORE any optimistic updates in this batch
      // Only capture on the first click of a rapid-click sequence
      if (rollbackQuantityRef.current === null) {
        rollbackQuantityRef.current = quantity;
      }

      // Always do optimistic update for better user experience
      // The server response will correct any inconsistencies
      queryClient.setQueryData<CartResponse>(
        ["cart", cartToken, paymentMethodsIncludes],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            cart: {
              ...old.cart,
              items: old.cart.items.map((item) =>
                item.id === itemId ? { ...item, quantity: newQuantity } : item,
              ),
            },
          };
        },
      );

      // Cancel any pending update and trigger debounced update
      debouncedUpdateQuantity(newQuantity);
    }
  };

  // early return for enrollment packs (deleteOnly items)
  if (deleteOnly) {
    // CURRENT-1427: hide trash once cart is locked (authorized + enrollment_pack).
    // getQueryData is non-reactive but fine — the navigation that locks the cart
    // re-mounts this component; backend 410 (fluid#18334) is the source of truth.
    const cart = queryClient.getQueryData<CartResponse>([
      "cart",
      cartToken,
      paymentMethodsIncludes,
    ])?.cart;
    if (cart?.state === "authorized" && cart?.enrollment_pack_id != null) {
      return null;
    }

    return (
      <div
        className={
          actionDisabled
            ? "pointer-events-none cursor-not-allowed opacity-50"
            : ""
        }
      >
        <button
          onClick={() =>
            removeEnrollmentItem.mutate({
              itemId: itemId.toString(),
            })
          }
          className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-600 hover:text-gray-800"
        >
          <FluidImage
            src={`${FLUID_CDN_URL}/images/checkout/icons/trash.svg`}
            alt="trash"
            width={10}
            height={10}
            className="h-3 w-3"
          />
        </button>
      </div>
    );
  }

  // regular item counter UI
  return (
    <div
      className={`flex h-7.5 max-w-24 min-w-20 items-center justify-between rounded-2xl border border-gray-100 sm:max-w-[90px] ${
        actionDisabled
          ? "pointer-events-none cursor-not-allowed opacity-50"
          : ""
      }`}
    >
      <button
        type="button"
        onClick={() => handleQuantityChange(-1)}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-gray-600 shadow-xs hover:text-gray-800"
      >
        {quantity > 1 ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="black"
            className="h-3 w-3"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M18 12H6"
            />
          </svg>
        ) : (
          <FluidImage
            src={`${FLUID_CDN_URL}/images/checkout/icons/trash.svg`}
            alt="trash"
            width={10}
            height={10}
            className="h-3 w-3"
          />
        )}
      </button>

      <span className="text-xs font-semibold text-gray-900">{quantity}</span>

      <button
        type="button"
        onClick={() => handleQuantityChange(1)}
        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-white text-gray-600 shadow-xs hover:bg-gray-50 hover:text-gray-800"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="h-3 w-3"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M12 6v12m6-6H6"
          />
        </svg>
      </button>
    </div>
  );
}
